import { useEffect, useMemo, useState } from "react";
import { calculateDynamicPayment } from "./calculateDynamicPayment";
import { solveNumberOfPeriods } from "./solveNumberOfPeriods";
import { useFormikContext } from "formik";

// Utilidades de formatação
const fmtBRL = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
    v || 0
  );

const fmtPct = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "percent", maximumFractionDigits: 3 }).format(
    v || 0
  );


export default function LoanRateCalculator({ totalValue }: { totalValue: number }) {
  const formik = useFormikContext(); // Use o tipo correto do seu form

  const [pv, setPv] = useState(0);
  const [weeklyRate, setWeeklyRate] = useState(0.05556);

  // ✅ Calcular parcela automaticamente baseada no valor emprestado
  const pmt = useMemo(() => calculateDynamicPayment(pv), [pv]);

  const n = useMemo(() => solveNumberOfPeriods(pv, pmt, weeklyRate), [pv, pmt, weeklyRate]);

  // ✅ Proteger contra valores inválidos
  const semanasArredondadas = isFinite(n) && n > 0 ? Math.ceil(n) : 0;
  const totalPago = semanasArredondadas > 0 ? pmt * semanasArredondadas : 0;
  const jurosAbsolutos = totalPago - pv;
  // Taxas equivalentes
  const monthlyRate = Math.pow(1 + weeklyRate, 52 / 12) - 1;
  const annualRate = Math.pow(1 + weeklyRate, 52) - 1;

  useEffect(() => {
    setPv(totalValue);
  }, [totalValue]);

  useEffect(() => {
    if (formik && pv > 0) {
      const calculationData = {
        principalValue: pv,
        weeklyRate: weeklyRate,
        paymentAmount: pmt,
        numberOfWeeks: isFinite(n) ? n : 0,
        numberOfWeeksRounded: semanasArredondadas,
        totalAmount: totalPago,
        totalInterest: jurosAbsolutos,
        totalToPrincipalRatio: totalPago > 0 && pv > 0 ? totalPago / pv : 0,
        ruleApplied: pv > 1750 ? "high" : "standard",
        monthlyEquivalentRate: monthlyRate,
        annualEquivalentRate: annualRate,
        calculatedAt: new Date(),
        isCalculationValid: semanasArredondadas > 0 && totalPago > 0
      };

      formik.setFieldValue('loanCalculation', calculationData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pv, weeklyRate, pmt, n, semanasArredondadas, totalPago, jurosAbsolutos, monthlyRate, annualRate]);

  const minPaymentNeeded = pv * weeklyRate;
  const isPaymentSufficient = pmt > minPaymentNeeded;

  return (
    <div className="w-full p-6 space-y-6">
      <h1 className="text-xl font-bold text-center">Calculadora de Parcelas do empréstimo</h1>

      {/* Layout em duas colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* COLUNA ESQUERDA */}
        <div className="space-y-4 ">
          {/* Regras de Parcelas */}
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="space-y-2 text-sm text-center">
              <h3 className="font-semibold text-blue-800 mb-2">📋 Regras de Parcelas</h3>
              <div className="flex items-center justify-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                <span>Até R$ 1.750,00: <strong>R$ 100/semana</strong></span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <span className="w-3 h-3 bg-blue-500 rounded-full"></span>
                <span>Acima de R$ 1.750,00: <strong>R$ 150/semana</strong></span>
              </div>
            </div>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-1 gap-4">
            <label className="grid gap-1">
              <span className="text-sm text-gray-600">Valor emprestado (PV)</span>
              <input
                type="number"
                step={0.01}
                className="border rounded-xl px-3 py-2"
                value={pv}
                onChange={(e) => setPv(Number(e.target.value))}
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm text-gray-600">Parcela (Calculada)</span>
              <input
                type="number"
                step={0.01}
                className="border rounded-xl px-3 py-2 bg-gray-100"
                value={pmt}
                readOnly
                title="Parcela calculada automaticamente baseada no valor emprestado"
              />
            </label>
            <label className="grid gap-1">
              <span className="text-sm text-gray-600">Taxa semanal (%)</span>
              <input
                type="number"
                step={0.001}
                className="border rounded-xl px-3 py-2"
                value={(weeklyRate * 100).toFixed(3)}
                onChange={(e) => setWeeklyRate(Number(e.target.value) / 100)}
              />
            </label>
          </div>

          {/* Indicador da regra aplicada */}
          <div className={`rounded-xl p-3 ${pv > 1750 ? 'bg-blue-100 border-blue-200' : 'bg-green-100 border-green-200'} border`}>
            <div className="text-center">
              <span className="text-sm text-gray-600">
                {pv > 1750 ? '🔵 Regra aplicada: Valor alto' : '🟢 Regra aplicada: Valor padrão'}
              </span>
              <div className="text-lg font-bold">
                Parcela: {fmtBRL(pmt)} por semana
              </div>
            </div>
          </div>
        </div>

        {/* COLUNA DIREITA */}
        <div className="space-y-4">
          {/* Resultados */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h2 className="font-semibold mb-3">Resultados</h2>
            <div className="grid grid-cols-1 gap-3">
              <div className="rounded-xl border bg-white p-3">
                <div className="text-xs text-gray-500">Número de semanas</div>
                <div className="text-lg font-medium">
                  {semanasArredondadas > 0 ? n.toFixed(2) : "—"}
                </div>
              </div>
              <div className="rounded-xl border bg-white p-3">
                <div className="text-xs text-gray-500">Parcelas arredondadas</div>
                <div className="text-lg font-medium">
                  {semanasArredondadas > 0 ? semanasArredondadas : "—"}
                </div>
              </div>
              <div className="rounded-xl border bg-white p-3">
                <div className="text-xs text-gray-500">Total pago</div>
                <div className="text-lg font-medium">
                  {totalPago > 0 ? fmtBRL(totalPago) : "—"}
                </div>
              </div>
              <div className="rounded-xl border bg-white p-3">
                <div className="text-xs text-gray-500">Juros absolutos</div>
                <div className="text-lg font-medium text-orange-600">
                  {totalPago > 0 ? fmtBRL(jurosAbsolutos) : "—"}
                </div>
              </div>
              <div className="rounded-xl border bg-white p-3">
                <div className="text-xs text-gray-500">Relação total/PV</div>
                <div className="text-lg font-medium">
                  {totalPago > 0 && pv > 0 ? `${(totalPago / pv).toFixed(3)}x` : "—"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!isPaymentSufficient && pv > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="text-yellow-800 font-semibold">⚠️ Atenção</div>
          <div className="text-yellow-700 text-sm">
            Parcela de {fmtBRL(pmt)} é baixa para o valor de {fmtBRL(pv)} com taxa de {fmtPct(weeklyRate)}/semana.
          </div>
          <div className="text-yellow-700 text-sm">
            <strong>Pagamento mínimo dos juros:</strong> {fmtBRL(minPaymentNeeded)}/semana
          </div>
          <div className="text-yellow-700 text-sm">
            <strong>Resultado:</strong> Prazo muito longo estimado em {semanasArredondadas} semanas
          </div>
        </div>
      )}

      <p className="text-sm text-gray-600 text-center">
        Esta calculadora ajusta automaticamente o valor da parcela baseado no valor emprestado.
        Taxa fixa de {fmtPct(weeklyRate)} por semana.
      </p>
    </div>
  );
}